"use client"
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { logger } from '@/lib/logger';

// Define the interface for our blog post structure
interface BlogPost {
  _uid: string;
  title: string;
  slug: string;
  description: string;
  date: string;
  thumb: string;
  topics: string;
  services: string;
  author_name: string;
  author_photo: string;
}

interface BlogContextProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedTopic: string;
  setSelectedTopic: (topic: string) => void;
  isSearching: boolean;
  totalResults: number;
  filteredPosts: BlogPost[];
  visiblePosts: BlogPost[];
  hasMore: boolean;
  loadMore: () => void;
  resetFilters: () => void;
  availableTopics: string[];
  topicCounts: Record<string, number>;
  updateBlogPosts: (posts: any[]) => void;
}

interface BlogProviderProps {
  allBlogPosts: any[];
  children: React.ReactNode;
}

const BlogContext = createContext<BlogContextProps | undefined>(undefined);

export const useBlogContext = () => {
  const context = useContext(BlogContext);
  if (context === undefined) {
    throw new Error('useBlogContext must be used within a BlogProvider');
  }
  return context;
};

export const BlogProvider: React.FC<BlogProviderProps> = ({ allBlogPosts: initialPosts, children }) => {
  const [allBlogPosts, setAllBlogPosts] = useState<any[]>(initialPosts || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [filteredPosts, setFilteredPosts] = useState<any[]>([]);
  const [visiblePosts, setVisiblePosts] = useState<any[]>([]);
  const [visibleCount, setVisibleCount] = useState(9);
  const [hasMore, setHasMore] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [topicCounts, setTopicCounts] = useState<Record<string, number>>({});
  const [availableTopics, setAvailableTopics] = useState<string[]>([]);

  // Log the initial posts for debugging
  // useEffect(() => {
  //   // logger.log("Initial BlogProvider posts count:", initialPosts?.length || 0);
  //   if (initialPosts?.length > 0) {
  //     logger.log("Initial post sample:",
  //       JSON.stringify({
  //         has_topics: !!initialPosts[0].topics,
  //         topics_value: initialPosts[0].topics,
  //         has_content: !!initialPosts[0].content,
  //         has_blog_details: initialPosts[0].content?.blog_details?.length > 0,
  //         nested_topics: initialPosts[0].content?.blog_details?.[0]?.topics,
  //       })
  //     );
  //   }
  // }, [initialPosts]);

  // Method to update blog posts and extract topics
  const updateBlogPosts = useCallback((posts: any[]) => {
    logger.log("updateBlogPosts called with", posts?.length || 0, "posts");

    if (!Array.isArray(posts) || posts.length === 0) {
      logger.log("No posts to process");
      return;
    }

    setAllBlogPosts(posts);

    // Extract and count topics from the posts
    const newTopicCounts: Record<string, number> = {};
    let extractedTopics = 0;

    // Process each post to find topics
    posts.forEach((post, index) => {
      let topicValue = '';

      // Try to get topics directly (streamlined format)
      if (post.topics) {
        topicValue = post.topics;
      }
      // Fall back to nested structure if needed
      else if (post.content?.blog_details?.[0]?.topics) {
        topicValue = post.content.blog_details[0].topics;
      }

      // Only process non-empty topics
      if (topicValue && typeof topicValue === 'string' && topicValue.trim()) {
        extractedTopics++;
        const topic = topicValue.trim();
        newTopicCounts[topic] = (newTopicCounts[topic] || 0) + 1;
      }

      // Log some samples for debugging
      if (index < 3) {
        logger.log(`Post ${index} topic:`, topicValue);
      }
    });

    logger.log("Extracted topics from", extractedTopics, "posts out of", posts.length);
    logger.log("Unique topics count:", Object.keys(newTopicCounts).length);
    logger.log("Topic counts:", newTopicCounts);

    // Update state with the topic information
    setTopicCounts(newTopicCounts);
    setAvailableTopics(Object.keys(newTopicCounts).sort());
  }, []);

  // Initialize topics when component mounts
  useEffect(() => {
    if (initialPosts?.length > 0) {
      updateBlogPosts(initialPosts);
    }
  }, [initialPosts, updateBlogPosts]);

  // Filter posts based on search term and topic
  const filterPosts = useCallback(() => {
    if (!Array.isArray(allBlogPosts) || allBlogPosts.length === 0) {
      setFilteredPosts([]);
      return;
    }

    const filtered = allBlogPosts.filter((post) => {
      let blogTitle = '';
      let blogTopics = '';

      // Handle both streamlined and original formats
      if (post.title) {
        // Streamlined format
        blogTitle = post.title;
        blogTopics = post.topics || '';
      } else {
        // Original nested format
        const { content, name } = post;
        if (!content || !Array.isArray(content.blog_details) || content.blog_details.length === 0) {
          return false;
        }

        const blogDetails = content.blog_details[0];
        blogTitle = name || blogDetails.title || '';
        blogTopics = blogDetails.topics || '';
      }

      // Filter by topic if selected
      const topicMatch = selectedTopic
        ? blogTopics.toLowerCase().replace(/\s/g, '-') === selectedTopic.toLowerCase()
        : true;

      // Filter by search term if provided
      const searchMatch = searchTerm
        ? blogTitle.toLowerCase().includes(searchTerm.toLowerCase())
        : true;

      return topicMatch && searchMatch;
    }).sort((a, b) => {
      const dateA = a.content?.blog_details?.[0]?.date || '';
      const dateB = b.content?.blog_details?.[0]?.date || '';
      return dateB.localeCompare(dateA);
    });

    setFilteredPosts(filtered);
    setHasMore(filtered.length > visibleCount);

    // Analytics tracking
    if (searchTerm || selectedTopic) {
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'blog_search',
          ...(searchTerm && { filter: searchTerm }),
          ...(selectedTopic && { topic: selectedTopic })
        });
      }
    }
  }, [allBlogPosts, searchTerm, selectedTopic, visibleCount]);


  // Update visible posts whenever filters change or load more is triggered
  useEffect(() => {
    filterPosts();
  }, [filterPosts, searchTerm, selectedTopic, allBlogPosts]);

  // Update the visible posts whenever filtered posts or visible count changes
  useEffect(() => {
    setVisiblePosts(filteredPosts.slice(0, visibleCount));
    setHasMore(filteredPosts.length > visibleCount);
  }, [filteredPosts, visibleCount]);

  // Set searching status based on whether there's a search term or topic
  useEffect(() => {
    setIsSearching(searchTerm !== '' || selectedTopic !== '');
  }, [searchTerm, selectedTopic]);

  // Load more posts
  const loadMore = useCallback(() => {
    setVisibleCount((prevCount) => prevCount + 6); // Load 6 more items
  }, []);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedTopic('');
    setVisibleCount(9); // Reset to initial count
  }, []);

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore) {
          loadMore();
        }
      },
      { rootMargin: '100px' }
    );

    const loadMoreElement = document.getElementById('load-more-trigger');
    if (loadMoreElement) {
      observer.observe(loadMoreElement);
    }

    return () => {
      if (loadMoreElement) {
        observer.unobserve(loadMoreElement);
      }
    };
  }, [hasMore, loadMore]);

  // Debug output of current state
  // useEffect(() => {
  //   logger.log("Current topics state:", {
  //     availableTopics,
  //     topicCounts,
  //     filteredPosts: filteredPosts.length,
  //     visiblePosts: visiblePosts.length
  //   });
  // }, [availableTopics, topicCounts, filteredPosts.length, visiblePosts.length]);

  return (
    <BlogContext.Provider
      value={{
        searchTerm,
        setSearchTerm,
        selectedTopic,
        setSelectedTopic,
        isSearching,
        totalResults: filteredPosts.length,
        filteredPosts,
        visiblePosts,
        hasMore,
        loadMore,
        resetFilters,
        availableTopics,
        topicCounts,
        updateBlogPosts
      }}
    >
      {children}
    </BlogContext.Provider>
  );
};